import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClientProvider } from "@tanstack/react-query";
import App from "./App";
import { AppErrorBoundary } from "@/components/common/AppErrorBoundary";
import { queryClient } from "./lib/api/queryClient";
import "./styles/fonts.css";
import "./styles/globals.css";
import { applyThemeToDocument, type Theme } from "./store/themeStore";
import { bootstrapBranding } from "./store/brandingStore";

// Must equal the `theme` default in `themeStore.ts` AND the inline bootstrap in
// `index.html`. That inline copy is what actually prevents the flash — it runs
// before the first paint, where this one cannot: this module only executes once
// the entry chunk has parsed. This remains as the authoritative pass (it also
// handles `system` re-resolution) and as the fallback if the inline script is
// ever removed.
const DEFAULT_THEME: Theme = "dark-glass";

// Match persisted Zustand shape (`{ state: { theme } }`) before React runs — avoids a flash of wrong theme.
const initializeTheme = () => {
  const stored = localStorage.getItem("everythingcloud-theme");
  if (!stored) {
    applyThemeToDocument(DEFAULT_THEME);
    return;
  }
  try {
    const { state } = JSON.parse(stored) as { state?: { theme?: Theme } };
    const theme = state?.theme;
    if (theme && typeof theme === "string") {
      applyThemeToDocument(theme);
    } else {
      applyThemeToDocument(DEFAULT_THEME);
    }
  } catch {
    applyThemeToDocument(DEFAULT_THEME);
  }
};

initializeTheme();

// Apply persisted distributor branding (colors, favicon, title) before React
// mounts — prevents a flash of the default theme on hard refresh. No-op when the
// user has never signed into a brandinged distributor on this device.
bootstrapBranding();

// The static shell painted from `index.html`. Removed explicitly rather than
// relying on React clearing the container, so there is never a frame with the
// shell and the real app both mounted.
document.getElementById("app-shell")?.remove();

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    {/*
      StrictMode stays. It double-invokes effects in development on purpose, to
      surface missing cleanup, and removing it to quieten the network tab would
      hide real bugs to make a symptom go away. The query cache below is what
      actually collapses the duplicates: two requests for the same key while one
      is in flight are deduplicated into a single fetch.
    */}
    {/*
      Outside the providers, because a crash inside one of them is exactly the
      case that used to paint a blank page, and a boundary nested under the
      thing that failed cannot catch it.
    */}
    <AppErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </AppErrorBoundary>
  </React.StrictMode>,
);
